import React from "react";
import {
  Dialog,
  IconButton,
  Box,
  Typography,
  Chip,
  List,
  ListItem,
  ListItemText,
  useTheme,
  useMediaQuery,
  ListItemIcon,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import InputOutlinedIcon from '@mui/icons-material/InputOutlined';
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import RoomOutlinedIcon from "@mui/icons-material/RoomOutlined";
import { tokens } from "../../../theme";
import { formatShippingTrackingDateTime } from "../../../utils/Utility";
import { ReactComponent as DragHandle } from "../../../assets/DragHandle.svg";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import { getOrderTrackingLogs, useOrderSlice } from "../../../redux/slice/order/OrderSlice";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { useModalStyles } from "../../common/useModalStyles";

interface Address {
  city: string | null;
  state: string | null;
  postal_code: string | null;
}

interface TrackingEvent {
  time_iso: string;
  description: string;
  location: string | null;
  address: Address;
  stage: string | null;
}

interface ShipmentProps {
  open: boolean;
  onClose: () => void;
  trackingNumber: string;
  orderId: number;
  orderNumber: string;
  shipper: Address;
  recipient: Address;
  status: string;
  carrierName: string;
  shippingDate: string;
  departureTime: string;
  expectedArrival: string;
  events: TrackingEvent[];
  milestones: string[];
}

const formatAddress = (addr?: Address | null) => {
  if (!addr) return "N/A";
  const city = addr.city ?? "";
  const state = addr.state ?? "";
  const postal_code = addr.postal_code ?? "";

  // Filter out empty strings and join with a comma
  return [city, state, postal_code].filter((part) => part).join(", ");
};

const ShipmentInfoPopup: React.FC<ShipmentProps> = ({
  open,
  onClose,
  trackingNumber,
  orderNumber,
  orderId,
  shipper,
  recipient,
  status,
  carrierName,
  shippingDate,
  departureTime,
  expectedArrival,
  events,
  milestones,
}) => {
  const progressIcons = [
    Inventory2OutlinedIcon,
    DragHandle ,
    LocalShippingOutlinedIcon,
    RoomOutlinedIcon,
  ];

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isDark = theme.palette.mode === "dark";
  const colors = tokens(theme.palette.mode);
  const {
    tokens: modalTokens,
    paperSx,
    headerSx,
    bodySx,
    iconBoxSx,
    closeIconSx,
  } = useModalStyles();
  const activeStep = events.length >= 4 ? 3 : events.length - 1;

  const addresses = [
    `From: ${formatAddress(shipper)}`,
    `To: ${formatAddress(recipient)}`,
  ];

  // Carrier URLs mapping
  const carrierUrls: { [key: string]: string } = {
    FEDEX: `https://fedex.com/fedextrack/?trknbr=${trackingNumber}`,
    USPS: `https://tools.usps.com/go/TrackConfirmAction?tLc=3&text28777=&tLabels=${trackingNumber}%2C%2C`,
    DHL: `https://www.dhl.com/us-en/home/tracking.html?tracking-id=${trackingNumber}&submit=1&inputsource=marketingstage`,
    UPS: `https://www.ups.com/track?tracknum=${trackingNumber}&loc=en_US&requester=QUIC/`,
  };

  // Handle carrier chip click
  const handleCarrierClick = () => {
    const url = carrierUrls[carrierName.toUpperCase()];
    if (url) {
      window.open(url, '_blank');
    }
  };

  const sectionCardSx = {
    p: 2,
    borderRadius: 3,
    backgroundColor: isDark ? "rgba(255,255,255,0.03)" : "#f8fafc",
    border: `1px solid ${modalTokens.border}`,
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{ sx: paperSx(isMobile) }}
    >
      {/* ── Header ── */}
      <Box sx={headerSx}>
        <Box sx={iconBoxSx("#14b8a6")}>
          <LocalShippingIcon sx={{ fontSize: 22, color: "#14b8a6" }} />
        </Box>

        <Box sx={{ flex: 1, minWidth: 0, pr: 3 }}>
          <Typography
            variant="h6"
            component="h2"
            sx={{
              color: modalTokens.textPrimary,
              lineHeight: 1.25,
            }}
          >
            Shipment Tracking
          </Typography>
          <Typography
            sx={{
              fontSize: "0.8rem",
              color: modalTokens.textSecondary,
              mt: 0.15,
            }}
          >
            Track your shipment status and delivery details
          </Typography>
        </Box>

        <IconButton onClick={onClose} size="small" sx={closeIconSx}>
          <CloseIcon sx={{ fontSize: "1rem" }} />
        </IconButton>
      </Box>

      {/* ── Body ── */}
      <Box
        sx={{
          ...bodySx,
          p: 2,
          gap: 0,
          minHeight: 0,
          overflowY: "auto",
          overflowX: "hidden",
          scrollbarGutter: "stable",
          "&::-webkit-scrollbar": { width: 6, height: 6 },
          "&::-webkit-scrollbar-track": { background: "transparent" },
          "&::-webkit-scrollbar-thumb": {
            background: theme.palette.mode === "dark" ? "rgba(255,255,255,0.12)" : "#CBD5E1",
            borderRadius: 4,
          },
        }}
      >
        {/* ── Header Section ── */}
        <Box sx={{ ...sectionCardSx, mb: 2 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            <Box>
              <Typography variant="h6" sx={{ color: modalTokens.textPrimary }}>
                Tracking Number: {trackingNumber}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Shipping date: {shippingDate} · Order Number: {orderNumber}
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={1.5}>
              {status !== "" ? (
                <Chip
                  label={status}
                  color={status === "Delivered" ? "success" : "warning"}
                  sx={{ fontWeight: "bold" }}
                />
              ) : null}
              <Chip
                icon={<LocalShippingIcon />}
                label={carrierName}
                variant="outlined"
                onClick={handleCarrierClick}
                sx={{
                  cursor: "pointer",
                  "&:hover": {
                    backgroundColor: theme.palette.action.hover,
                  },
                }}
              />
            </Box>
          </Box>
        </Box>

        {status !== "NotFound" && status !== "" ? (
          <>
            {/* ── Combined Middle Section: Locations & Timeline ── */}
            <Box sx={{ ...sectionCardSx, mb: 1 }}>
              {/* Locations */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  position: "relative",
                  pl: 3,
                  mt: 1,
                  mb: 1,
                }}
              >
                <Box
                  position="absolute"
                  top="12px"
                  left="27.5px"
                  height="18px"
                  borderLeft={`1px solid ${modalTokens.border}`}
                  borderRadius={10}
                  zIndex={0}
                />
                {addresses.map((address, index) => (
                  <Box key={index} display="flex" alignItems="center" mb={1}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        backgroundColor: modalTokens.textSecondary,
                        mr: 1.5,
                        zIndex: 1,
                      }}
                    />
                    <Typography variant="body2" sx={{ color: colors.grey[300] }}>
                      {address}
                    </Typography>
                  </Box>
                ))}
              </Box>

              {/* Timeline */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  {progressIcons.map((Icon, index) => (
                    <Box key={index} sx={{ display: "flex", alignItems: "center" }}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: "50%",
                          backgroundColor:
                            index <= activeStep
                              ? modalTokens.accent
                              : modalTokens.border,
                          color:
                            index <= activeStep
                              ? "#fff"
                              : modalTokens.textSecondary,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          boxShadow: index <= activeStep
                            ? "0 0 0 4px rgba(20,184,166,0.15)"
                            : "none",
                        }}
                      >
                        <Icon fontSize="small" />
                      </Box>
                      {index < progressIcons.length - 1 && (
                        <Box
                          sx={{
                            height: 3,
                            background:
                              index < activeStep
                                ? `linear-gradient(to right, ${modalTokens.accent}, ${modalTokens.accent})`
                                : modalTokens.border,
                            width: { xs: 40, md: 120 },
                            borderRadius: 1.5,
                          }}
                        />
                      )}
                    </Box>
                  ))}
                </Box>
              </Box>

              {/* Time Labels */}
              <Box
                display="flex"
                justifyContent="space-between"
                flexWrap="wrap"
                mt={2}
              >
                <Box textAlign="center" flex={1}>
                  <Typography variant="body2" color="text.secondary">
                    Departure Time
                  </Typography>
                  <Typography variant="h6" sx={{ color: modalTokens.textPrimary }}>
                    {departureTime}
                  </Typography>
                </Box>
                <Divider orientation="vertical" flexItem sx={{ mx: 2 }} />
                <Box textAlign="center" flex={1}>
                  <Typography variant="body2" color="text.secondary">
                    {status === "Delivered" ? "Actual Delivery Time" : "Expected Arrival"}
                  </Typography>
                  <Typography variant="h6" sx={{ color: modalTokens.textPrimary }}>
                    {expectedArrival}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* ── Shipment Status Section ── */}
            <Box sx={sectionCardSx}>
              <Typography variant="h6" gutterBottom fontWeight="bold" sx={{ color: modalTokens.textPrimary }}>
                Shipment Status
              </Typography>

              <List sx={{ p: 0 }}>
                {events.map((event, index) => (
                  <ListItem
                    key={index}
                    alignItems="flex-start"
                    sx={{ position: "relative", pl: 0 }}
                  >
                    {index !== events.length - 1 && (
                      <Box
                        sx={{
                          position: "absolute",
                          top: 30,
                          bottom: 0,
                          left: 10,
                          width: "2px",
                          backgroundColor: modalTokens.accent,
                          zIndex: 0,
                          height: 80,
                        }}
                      />
                    )}

                    <ListItemIcon sx={{ minWidth: 40, zIndex: 1 }}>
                      <CheckCircleIcon
                        sx={{
                          color: modalTokens.accent,
                          backgroundColor: isDark ? modalTokens.bg : "#fff",
                          borderRadius: "50%",
                          zIndex: 1,
                        }}
                      />
                    </ListItemIcon>

                    <ListItemText
                      primary={
                        <Typography variant="body2" fontWeight={600} sx={{ color: modalTokens.textPrimary }}>
                          {formatShippingTrackingDateTime(event.time_iso)}
                        </Typography>
                      }
                      secondary={
                        <>
                          <Typography variant="body2" sx={{ color: modalTokens.textPrimary }}>
                            {event.description}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {event.location || formatAddress(event.address)}
                          </Typography>
                        </>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          </>
        ) : (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <ErrorOutlineIcon
              color={status === "NotFound" ? "error" : "warning"}
              sx={{ fontSize: 20 }}
            />
            {status === "NotFound" ? (
              <Typography variant="body1" color="error">
                The tracking number you entered seems incorrect. Please check the number and try again.
              </Typography>
            ) : (
              <Typography variant="body1" sx={{ color: "warning.main" }}>
                Your tracking information is still being processed. Please check back later.
              </Typography>
            )}
          </Box>
        )}
      </Box>
    </Dialog>
  );
};

export default ShipmentInfoPopup;
